import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth, useAdmin } from '@/auth/AuthProvider';
import { useUnreadCount } from '@/hooks/useAdminData';
import { useUnreadMessages } from '@/hooks/useMessaging';
import { usePendingRequestCount } from '@/hooks/usePendingRequestCount';
import { BRAND } from '@crew/shared';
import { BrandLockup } from '../brand/BrandLogo';
import { BellIcon, ChatIcon, LogoutIcon } from '../icons';
import { MOBILE_MORE, PRIMARY_NAV, secondaryNavFor, type NavItem } from './navItems';

/**
 * מעטפת הניהול.
 * נייד: פס עליון דק + ניווט תחתון בהישג האגודל.
 * מחשב: סרגל צד כחול-לילה בצד ההתחלה (ימין, ב-RTL), עם הלוגו.
 */
export function AppShell() {
  const pending = usePendingRequestCount();
  const unread = useUnreadCount();
  const messages = useUnreadMessages().total;
  const badges = { pendingRequests: pending, unreadNotifications: unread, unreadMessages: messages };

  return (
    <div className="min-h-dvh md:flex">
      <SideNav badges={badges} />
      <div className="min-w-0 flex-1">
        <MobileTopBar unread={unread} messages={messages} />
        <main className="mx-auto w-full max-w-5xl px-4 pb-28 pt-4 md:px-8 md:pb-12 md:pt-8">
          <Outlet />
        </main>
      </div>
      <BottomNav badges={badges} />
    </div>
  );
}

type Badges = Record<NonNullable<NavItem['badge']>, number>;

function Badge({ value }: { value: number }) {
  if (value <= 0) return null;
  return (
    <span className="tabular min-w-5 rounded-full bg-danger px-1.5 text-center text-[11px] font-bold leading-5 text-white">
      {value > 99 ? '99+' : value}
    </span>
  );
}

function SideNav({ badges }: { badges: Badges }) {
  const { me } = useAdmin();
  const { signOut } = useAuth();
  const link = (item: NavItem) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end ?? false}
      className={({ isActive }) =>
        [
          'relative flex min-h-11 items-center gap-3 rounded-control px-3 text-[15px] font-medium transition-colors',
          isActive
            ? 'bg-white/10 text-white before:absolute before:inset-y-2 before:start-0 before:w-[3px] before:rounded-full before:bg-accent'
            : 'text-steel hover:bg-white/5 hover:text-white',
        ].join(' ')
      }
    >
      {item.icon}
      <span className="flex-1">{item.label}</span>
      {item.badge && <Badge value={badges[item.badge]} />}
    </NavLink>
  );

  return (
    <aside className="sticky top-0 hidden h-dvh w-64 shrink-0 flex-col bg-brand px-4 py-6 md:flex">
      <div className="mb-8 px-1">
        <BrandLockup size={52} tone="light" />
        <p className="mt-3 border-t border-white/10 pt-3 text-[12.5px] leading-snug text-steel">{BRAND.systemLine}</p>
      </div>
      <nav aria-label="ניווט ראשי" className="space-y-1">
        {PRIMARY_NAV.map(link)}
      </nav>
      <div className="my-4 border-t border-white/10" />
      <nav aria-label="ניווט משני" className="space-y-1">
        {secondaryNavFor(me.role).map(link)}
      </nav>
      <div className="mt-auto rounded-card bg-brand-raised p-3">
        <div className="truncate text-[14px] font-semibold text-white">{me.displayName}</div>
        <div className="ltr truncate text-start text-[12px] text-steel">{me.email}</div>
        <button
          type="button"
          onClick={() => void signOut()}
          className="mt-2 flex items-center gap-2 text-[13px] font-medium text-steel hover:text-white"
        >
          <LogoutIcon size={16} />
          יציאה
        </button>
      </div>
    </aside>
  );
}

function MobileTopBar({ unread, messages }: { unread: number; messages: number }) {
  const location = useLocation();
  return (
    <header className="pt-safe sticky top-0 z-20 border-b-2 border-accent bg-brand md:hidden">
      <div className="flex h-14 items-center justify-between px-4">
        <NavLink to="/" aria-label="מסך הבית">
          <BrandLockup size={38} tone="light" tagline={false} />
        </NavLink>
        <div className="flex items-center">
        <NavLink
          to="/messages"
          aria-label="הודעות"
          aria-current={location.pathname.startsWith('/messages') ? 'page' : undefined}
          className="relative flex size-11 items-center justify-center rounded-full text-steel hover:bg-white/10 hover:text-white"
        >
          <ChatIcon />
          {messages > 0 && (
            <span className="absolute end-1 top-1">
              <Badge value={messages} />
            </span>
          )}
        </NavLink>
        <NavLink
          to="/notifications"
          aria-label="התראות"
          aria-current={location.pathname === '/notifications' ? 'page' : undefined}
          className="relative flex size-11 items-center justify-center rounded-full text-steel hover:bg-white/10 hover:text-white"
        >
          <BellIcon />
          {unread > 0 && (
            <span className="absolute end-1 top-1">
              <Badge value={unread} />
            </span>
          )}
        </NavLink>
        </div>
      </div>
    </header>
  );
}

function BottomNav({ badges }: { badges: Badges }) {
  const items = [...PRIMARY_NAV, MOBILE_MORE];
  return (
    <nav
      aria-label="ניווט ראשי"
      className="pb-safe fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 backdrop-blur md:hidden"
    >
      <ul className="mx-auto grid max-w-lg grid-cols-5">
        {items.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={item.end ?? false}
              className={({ isActive }) =>
                [
                  'relative flex h-16 flex-col items-center justify-center gap-1 text-[12px] font-medium',
                  isActive ? 'text-brand font-semibold' : 'text-ink-3',
                ].join(' ')
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && <span aria-hidden="true" className="absolute top-0 h-[3px] w-8 rounded-b-full bg-accent" />}
                  <span className="relative">
                    {item.icon}
                    {item.badge && badges[item.badge] > 0 && (
                      <span className="absolute -end-2.5 -top-1.5">
                        <Badge value={badges[item.badge]} />
                      </span>
                    )}
                  </span>
                  {item.label}
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
